import { supabase } from './supabase';
import { ClientSideScrapingService } from './clientSideScrapingService';

export interface PriceHistory {
  id: string;
  item_id: string;
  price: number;
  currency: string;
  checked_at: string;
  check_type: 'automated' | 'manual';
  price_change?: number;
  created_at: string;
}

export interface PriceTrackingItem {
  id: string;
  title: string;
  original_url: string;
  price?: number;
  currency: string;
  price_tracking_enabled: boolean;
  tracking_priority: 'HIGH' | 'MEDIUM' | 'LOW';
  last_price_check?: string;
}

export class PriceTrackingService {
  /**
   * Enable price tracking for a wishlist item
   */
  static async enablePriceTracking(
    itemId: string, 
    priority: 'HIGH' | 'MEDIUM' | 'LOW' = 'LOW'
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .update({
          price_tracking_enabled: true,
          tracking_priority: priority,
          last_price_check: new Date().toISOString()
        })
        .eq('id', itemId);

      if (error) throw error;
      
      console.log('✅ Price tracking enabled for item:', itemId);
    } catch (error) {
      console.error('❌ Failed to enable price tracking:', error);
      throw error;
    }
  }

  /**
   * Disable price tracking for a wishlist item
   */
  static async disablePriceTracking(itemId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .update({
          price_tracking_enabled: false,
          tracking_priority: 'LOW'
        })
        .eq('id', itemId);

      if (error) throw error;
      
      console.log('✅ Price tracking disabled for item:', itemId);
    } catch (error) {
      console.error('❌ Failed to disable price tracking:', error);
      throw error;
    }
  }

  /**
   * Update tracking priority for a wishlist item
   */
  static async updateTrackingPriority(
    itemId: string, 
    priority: 'HIGH' | 'MEDIUM' | 'LOW'
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .update({ tracking_priority: priority })
        .eq('id', itemId);

      if (error) throw error;
      
      console.log('✅ Tracking priority updated for item:', itemId);
    } catch (error) {
      console.error('❌ Failed to update tracking priority:', error);
      throw error;
    }
  }

  /**
   * Manually check price for a specific item
   */
  static async manualPriceCheck(itemId: string): Promise<PriceHistory> {
    try {
      console.log('🔍 Starting manual price check for item:', itemId);

      // Get the item details
      const { data: item, error: itemError } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('id', itemId)
        .single();

      if (itemError || !item) {
        throw new Error('Item not found');
      }

      // Scrape the current price
      const scrapingResult = await ClientSideScrapingService.scrapeProduct(item.original_url);
      
      if (!scrapingResult || !scrapingResult.price) {
        throw new Error('Could not retrieve current price');
      }

      const newPrice = scrapingResult.price;
      const currency = scrapingResult.currency || item.currency || 'USD';
      
      // Calculate price change
      const previousPrice = item.price;
      const priceChange = previousPrice ? newPrice - previousPrice : 0;

      // Update the item's current price
      const { error: updateError } = await supabase
        .from('wishlist_items')
        .update({
          price: newPrice,
          currency: currency,
          last_price_check: new Date().toISOString()
        })
        .eq('id', itemId);

      if (updateError) throw updateError;

      // Record in price history
      const { data: priceHistory, error: historyError } = await supabase
        .from('price_history')
        .insert({
          item_id: itemId,
          price: newPrice,
          currency: currency,
          check_type: 'manual',
          price_change: priceChange
        })
        .select()
        .single();

      if (historyError) throw historyError;

      console.log('✅ Manual price check completed:', {
        itemId,
        oldPrice: previousPrice,
        newPrice,
        priceChange
      });

      return priceHistory;
    } catch (error) {
      console.error('❌ Manual price check failed:', error);
      throw error;
    }
  }

  /**
   * Get price history for a specific item
   */
  static async getPriceHistory(itemId: string, limit: number = 30): Promise<PriceHistory[]> {
    try {
      const { data, error } = await supabase
        .from('price_history')
        .select('*')
        .eq('item_id', itemId)
        .order('checked_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Failed to get price history:', error);
      throw error;
    }
  }

  /**
   * Get all items with price tracking enabled
   */
  static async getTrackedItems(): Promise<PriceTrackingItem[]> {
    try {
      const { data, error } = await supabase
        .from('wishlist_items')
        .select('id, title, original_url, price, currency, price_tracking_enabled, tracking_priority, last_price_check')
        .eq('price_tracking_enabled', true)
        .order('tracking_priority', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Failed to get tracked items:', error);
      throw error;
    }
  }

  /**
   * Get items by tracking priority for automated checks
   */
  static async getItemsByPriority(priority: 'HIGH' | 'MEDIUM' | 'LOW'): Promise<PriceTrackingItem[]> {
    try {
      const { data, error } = await supabase
        .from('wishlist_items')
        .select('id, title, original_url, price, currency, price_tracking_enabled, tracking_priority, last_price_check')
        .eq('price_tracking_enabled', true)
        .eq('tracking_priority', priority);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Failed to get items by priority:', error);
      throw error;
    }
  }

  /**
   * Batch check prices for multiple items (for automated system)
   */
  static async batchPriceCheck(
    items: PriceTrackingItem[], 
    checkType: 'automated' | 'manual' = 'automated'
  ): Promise<{ success: number; failed: number; results: any[] }> {
    const results: any[] = [];
    let success = 0;
    let failed = 0;

    console.log(`🔄 Starting batch price check for ${items.length} items (${checkType})`);

    // Process items in batches of 5 to avoid overwhelming the system
    const batchSize = 5;
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      
      const batchPromises = batch.map(async (item) => {
        try {
          const scrapingResult = await ClientSideScrapingService.scrapeProduct(item.original_url);
          
          if (!scrapingResult || !scrapingResult.price) {
            throw new Error('Could not retrieve price');
          }

          const newPrice = scrapingResult.price;
          const currency = scrapingResult.currency || item.currency || 'USD';
          const priceChange = item.price ? newPrice - item.price : 0;

          // Update item price
          await supabase
            .from('wishlist_items')
            .update({
              price: newPrice,
              currency: currency,
              last_price_check: new Date().toISOString()
            })
            .eq('id', item.id);

          // Record in price history
          const { data: priceHistory } = await supabase
            .from('price_history')
            .insert({
              item_id: item.id,
              price: newPrice,
              currency: currency,
              check_type: checkType,
              price_change: priceChange
            })
            .select()
            .single();

          success++;
          return { itemId: item.id, success: true, priceHistory };
        } catch (error) {
          console.error(`❌ Failed to check price for item ${item.id}:`, error);
          failed++;
          return { itemId: item.id, success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
      });

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Rate limiting - wait 1 second between batches
      if (i + batchSize < items.length) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    console.log(`✅ Batch price check completed: ${success} success, ${failed} failed`);
    return { success, failed, results };
  }

  /**
   * Get price tracking statistics for a user
   */
  static async getTrackingStats(): Promise<{
    totalTracked: number;
    highPriority: number;
    mediumPriority: number;
    lowPriority: number;
    lastChecked: string | null;
  }> {
    try {
      const { data: trackedItems } = await supabase
        .from('wishlist_items')
        .select('tracking_priority, last_price_check')
        .eq('price_tracking_enabled', true);

      if (!trackedItems) {
        return {
          totalTracked: 0,
          highPriority: 0,
          mediumPriority: 0,
          lowPriority: 0,
          lastChecked: null
        };
      }

      const stats = {
        totalTracked: trackedItems.length,
        highPriority: trackedItems.filter(item => item.tracking_priority === 'HIGH').length,
        mediumPriority: trackedItems.filter(item => item.tracking_priority === 'MEDIUM').length,
        lowPriority: trackedItems.filter(item => item.tracking_priority === 'LOW').length,
        lastChecked: trackedItems
          .map(item => item.last_price_check)
          .filter(Boolean)
          .sort()
          .pop() || null
      };

      return stats;
    } catch (error) {
      console.error('❌ Failed to get tracking stats:', error);
      throw error;
    }
  }
}
